import { useState, type FormEvent, type ReactNode } from "react";
import { ArrowLeft, Check, Cloud, CloudOff, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { STEP_GROUPS, type StepGroup } from "@/lib/onboarding/stepMachine";
import type { SaveStatus } from "@/stores/onboardingStore";

interface WizardShellProps {
  stepId: string;
  group: StepGroup | null;
  saveStatus: SaveStatus;
  /** Shown with the error status: re-sends every failed save (nothing is dropped silently). */
  onRetry?: () => void;
  onBack?: () => void;
  /** Primary action; the whole screen is a form so Enter continues (§29). */
  onSubmit?: () => void;
  footer?: ReactNode;
  /** Desktop live preview (§26); on mobile it opens from the header. */
  preview?: ReactNode;
  children: ReactNode;
}

function SaveIndicator({ status, onRetry }: { status: SaveStatus; onRetry?: () => void }) {
  const { t } = useTranslation();
  if (status === "idle") return <span data-testid="onboarding-save-status" data-status="idle" />;
  const content =
    status === "saving" ? (
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        {t("onboarding.nav.saving")}
      </>
    ) : status === "saved" ? (
      <>
        <Cloud className="h-3.5 w-3.5" aria-hidden="true" />
        {t("onboarding.nav.saved")}
      </>
    ) : (
      <>
        <CloudOff className="h-3.5 w-3.5" aria-hidden="true" />
        {t("onboarding.nav.saveError")}
        {onRetry && (
          <Button type="button" variant="link" size="sm" className="h-auto p-0 text-destructive underline" onClick={onRetry} data-testid="onboarding-save-retry">
            {t("onboarding.nav.retry")}
          </Button>
        )}
      </>
    );
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs",
        status === "error" ? "text-destructive" : "text-muted-foreground",
      )}
      role="status"
      aria-live="polite"
      data-testid="onboarding-save-status"
      data-status={status}
    >
      {content}
    </span>
  );
}

function ProgressHeader({ group }: { group: StepGroup }) {
  const { t } = useTranslation();
  const index = STEP_GROUPS.indexOf(group);
  return (
    <div data-testid="onboarding-progress" data-group={group}>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t("onboarding.nav.stage", { current: index + 1, total: STEP_GROUPS.length })} · {t(`onboarding.groups.${group}`)}
      </p>
      <ol className="flex items-center gap-2" aria-label={t("onboarding.nav.stage", { current: index + 1, total: STEP_GROUPS.length })}>
        {STEP_GROUPS.map((g, i) => (
          <li key={g} className="flex flex-1 flex-col gap-1">
            <span
              className={cn("h-1.5 rounded-full", i < index ? "bg-pink-500" : i === index ? "bg-pink-400" : "bg-muted")}
              aria-hidden="true"
            />
            <span
              className={cn(
                "hidden items-center gap-1 text-[11px] sm:flex",
                i <= index ? "text-foreground" : "text-muted-foreground",
              )}
              aria-current={i === index ? "step" : undefined}
            >
              {i < index && <Check className="h-3 w-3 text-pink-500" aria-hidden="true" />}
              {t(`onboarding.groups.${g}`)}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * JUB-154 wizard frame (§25/§26): Back + save status on top, the four-stage
 * progress, one question per screen, a sticky primary action at the bottom
 * on mobile, and the live preview beside the form on desktop.
 */
export function WizardShell({ stepId, group, saveStatus, onRetry, onBack, onSubmit, footer, preview, children }: WizardShellProps) {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit?.();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/40 via-background to-background dark:from-pink-950/10">
      <div className={cn("mx-auto flex min-h-screen max-w-6xl gap-10 px-4 lg:px-8", preview ? "lg:grid lg:grid-cols-[minmax(0,1fr)_400px]" : "")}>
        <form
          onSubmit={handleSubmit}
          className="mx-auto flex w-full max-w-xl flex-col"
          data-testid="onboarding-step"
          data-step={stepId}
          noValidate
        >
          <header className="flex items-center justify-between gap-3 py-4">
            {onBack ? (
              <Button type="button" variant="ghost" size="sm" onClick={onBack} data-testid="onboarding-back" className="-ml-2">
                <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
                {t("onboarding.nav.back")}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <SaveIndicator status={saveStatus} onRetry={onRetry} />
              {preview && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="lg:hidden"
                  onClick={() => setPreviewOpen(true)}
                  data-testid="onboarding-preview-toggle"
                >
                  <Eye className="mr-1 h-4 w-4" aria-hidden="true" />
                  {t("onboarding.nav.previewToggle")}
                </Button>
              )}
            </div>
          </header>

          {group && <ProgressHeader group={group} />}

          <main className="flex-1 py-8 sm:flex-initial">{children}</main>

          {footer && (
            <footer className="sticky bottom-0 -mx-4 border-t bg-background/95 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:backdrop-blur-none">
              {footer}
            </footer>
          )}
        </form>

        {preview && (
          <aside className="sticky top-6 hidden h-fit py-6 lg:block" aria-label={t("onboarding.nav.previewLabel")}>
            {preview}
          </aside>
        )}
      </div>

      {preview && (
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-h-[92vh] max-w-[420px] overflow-hidden p-3">
            <DialogClose onClose={() => setPreviewOpen(false)} />
            <DialogHeader>
              <DialogTitle className="text-base">{t("onboarding.nav.previewLabel")}</DialogTitle>
            </DialogHeader>
            {previewOpen && preview}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
